import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, queryOptions, type QueryFunction } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useAuthedMutation } from "@/lib/authed-mutation";
import { globalQueryFn } from "@/lib/query-registry";
import { updateAiSettings, type AiSettings } from "@/lib/ai-settings.functions";

const MODEL_PRESETS = {
  anthropic: [
    "claude-sonnet-4-5-20250929",
    "claude-opus-4-1-20250805",
    "claude-3-5-haiku-20241022",
  ],
  lovable_ai: [
    "google/gemini-3.6-flash",
    "google/gemini-3.1-pro-preview",
    "openai/gpt-5.6-terra",
    "openai/gpt-5.6-luna",
  ],
} as const;

const aiSettingsQuery = () =>
  queryOptions({
    queryKey: ["admin.ai-settings.get"] as const,
    queryFn: globalQueryFn as QueryFunction<{ settings: AiSettings }>,
    staleTime: 0,
  });

export const Route = createFileRoute("/admin/settings/ai")({
  head: () => ({
    meta: [
      { title: "AI Settings · Admin · Shugert" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AiSettingsPage,
});

function AiSettingsPage() {
  const q = useQuery({ ...aiSettingsQuery(), retry: 1 });
  const settings = q.data?.settings;

  const [provider, setProvider] = useState<"anthropic" | "lovable_ai">("anthropic");
  const [modelStarter, setModelStarter] = useState("claude-sonnet-4-5-20250929");
  const [modelProResearch, setModelProResearch] = useState("claude-opus-4-1-20250805");
  const [modelProNarrative, setModelProNarrative] = useState("claude-opus-4-1-20250805");
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!settings) return;
    setProvider(settings.provider);
    setModelStarter(settings.model_starter);
    setModelProResearch(settings.model_pro_research);
    setModelProNarrative(settings.model_pro_narrative);
  }, [settings]);

  const m = useAuthedMutation(updateAiSettings, {
    invalidate: [["admin.ai-settings.get"]],
    onSuccess: () => {
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    },
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    m.mutate({
      provider,
      model_starter: modelStarter.trim(),
      model_pro_research: modelProResearch.trim(),
      model_pro_narrative: modelProNarrative.trim(),
    });
  };

  return (
    <div className="space-y-8">
      <header className="flex items-end justify-between gap-3">
        <div>
          <Link
            to="/admin/settings"
            className="text-xs text-muted-foreground hover:text-foreground"
          >
            ← Settings
          </Link>
          <h1 className="mt-2 font-display text-2xl font-semibold text-foreground">
            AI Provider & Models
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Controls which model powers the GEO audit pipeline. Changes take effect on the next paid
            order.
          </p>
        </div>
      </header>

      {q.isPending && <p className="text-sm text-muted-foreground">Loading…</p>}
      {q.isError && (
        <div className="space-y-2 rounded-2xl border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
          <p>
            Could not load the saved AI settings
            {q.error?.message ? `: ${q.error.message}` : "."}
          </p>
          <p className="text-xs opacity-80">
            The form below is editable and shows the current defaults. Saving will overwrite the
            stored configuration.
          </p>
          <button
            type="button"
            onClick={() => q.refetch()}
            className="rounded-full border border-destructive/40 px-3 py-1 text-xs"
          >
            Retry
          </button>
        </div>
      )}

      {!q.isPending && (
        <form
          onSubmit={onSubmit}
          className="space-y-6 rounded-2xl border border-border bg-card/30 p-6"
        >
          <div>
            <label className="text-[10px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
              Provider
            </label>
            <select
              value={provider}
              onChange={(e) => setProvider(e.target.value as "anthropic" | "lovable_ai")}
              className="mt-2 w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm text-foreground focus:border-foreground focus:outline-none"
            >
              <option value="anthropic">Anthropic (direct API)</option>
              <option value="lovable_ai">Lovable AI Gateway</option>
            </select>
            <p className="mt-2 text-xs text-muted-foreground">
              Anthropic uses your ANTHROPIC_API_KEY secret. Lovable AI uses LOVABLE_API_KEY.
            </p>
          </div>

          <Field
            label="Starter model"
            help={`Used for Starter-tier reports (≤ 50 pages). Default: claude-sonnet-4-5-20250929.`}
            value={modelStarter}
            onChange={setModelStarter}
            options={MODEL_PRESETS[provider]}
          />
          <Field
            label="Pro / Agency research model"
            help="Reserved for future deep-research phase (currently informational)."
            value={modelProResearch}
            onChange={setModelProResearch}
            options={MODEL_PRESETS[provider]}
          />
          <Field
            label="Pro / Agency narrative model"
            help={`Powers narrative + scoring for Pro/Agency (≤ 100 pages). Default: claude-opus-4-1-20250805.`}
            value={modelProNarrative}
            onChange={setModelProNarrative}
            options={MODEL_PRESETS[provider]}
          />

          {m.isError && (
            <p className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {m.error.message}
            </p>
          )}
          <div className="flex items-center gap-3">
            <button
              type="submit"
              disabled={m.isPending}
              className="rounded-full bg-foreground px-5 py-2 text-sm font-medium text-background hover:opacity-90 disabled:opacity-50"
            >
              {m.isPending ? "Saving…" : "Save"}
            </button>
            {saved && <span className="text-xs text-muted-foreground">Saved ✓</span>}
          </div>
        </form>
      )}

      <details className="rounded-2xl border border-border bg-card/20 p-5 text-sm">
        <summary className="cursor-pointer font-medium text-foreground">How this works</summary>
        <div className="mt-4 space-y-3 text-muted-foreground">
          <p>
            When a customer pays, the Stripe webhook flips the order to <code>processing</code> and
            invokes the <code>run-audit</code> Edge Function. That function:
          </p>
          <ol className="ml-5 list-decimal space-y-1.5">
            <li>Crawls the customer's site (sitemap-first, BFS fallback) up to the tier cap.</li>
            <li>
              Runs deterministic checks: llms.txt, robots.txt, sitemap, PageSpeed, on-page SEO,
              JSON-LD, content depth, and (Pro/Agency) competitor sweep.
            </li>
            <li>Asks the configured model for a narrative + per-section scoring.</li>
            <li>
              Renders a DOCX, uploads to the private <code>audit-reports</code> bucket, and writes{" "}
              <code>audit_sections</code> rows.
            </li>
            <li>
              <strong>Starter</strong> → status <code>ready</code>, email sent.
              <br />
              <strong>Pro / Agency</strong> → status <code>review_needed</code>; you flip to{" "}
              <code>ready</code> from the order page to release the email.
            </li>
          </ol>
          <p>
            Reports stay in the bucket for 6 months. The portal generates a fresh 1-hour signed URL
            on every download.
          </p>
        </div>
      </details>
    </div>
  );
}

function Field({
  label,
  help,
  value,
  onChange,
  options,
}: {
  label: string;
  help: string;
  value: string;
  onChange: (v: string) => void;
  options?: readonly string[];
}) {
  const listId = `models-${label.replace(/\W+/g, "-").toLowerCase()}`;
  return (
    <div>
      <label className="text-[10px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
        {label}
      </label>
      <input
        type="text"
        value={value}
        list={options ? listId : undefined}
        onChange={(e) => onChange(e.target.value)}
        className="mt-2 w-full rounded-xl border border-border bg-background px-3 py-2.5 font-mono text-sm text-foreground focus:border-foreground focus:outline-none"
      />
      {options && (
        <datalist id={listId}>
          {options.map((o) => (
            <option key={o} value={o} />
          ))}
        </datalist>
      )}
      <p className="mt-2 text-xs text-muted-foreground">{help}</p>
    </div>
  );
}
